import { useState, useRef, useEffect } from "react";
import { ReactComponent as Negrita } from "../../../../../assets/Negrita.svg";
import { ReactComponent as Cursive } from "../../../../../assets/Cursive.svg";
import { ReactComponent as Subrayado } from "../../../../../assets/Subrayado.svg";
import { ReactComponent as MiddleLine } from "../../../../../assets/MiddleLine.svg";
import { ReactComponent as LeftSentence } from "../../../../../assets/LeftSentence.svg";
import { ReactComponent as CenterSentence } from "../../../../../assets/CenterSentence.svg";
import { ReactComponent as RightSentence } from "../../../../../assets/RightSentence.svg";
import { ReactComponent as ViñetaNumerica } from "../../../../../assets/ViñetaNumerica.svg";
import { ReactComponent as ViñetaSimple } from "../../../../../assets/ViñetaSimple.svg";
import { ReactComponent as CodeSimbol } from "../../../../../assets/CodeSimbol.svg";
import { ReactComponent as EmojiSimbol } from "../../../../../assets/EmojiSimbol.svg";
import { ReactComponent as StarMagic } from "../../../../../assets/StarMagic.svg";
import { ReactComponent as A } from "../../../../../assets/A.svg";
import { ReactComponent as ArrowDownGray } from "../../../../../assets/arrowDownGray.svg";
import styles from "./MiniWordDocs.module.css";
import { useDispatch } from "react-redux";
import { sendEmailNotify } from "../../../../../../../actions/automate";

function MiniWordDocs({
  configuration,
  handleConfigurationChange,
  icon,
  whatsappConfiguration,
  initialContent,
  autoResize
}) {
  const [formatState, setFormatState] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikeThrough: false,
    justifyLeft: false,
    justifyCenter: false,
    justifyRight: false,
    fontName: "Arial",
    fontSize: "3",
  });
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [htmlContent, setHtmlContent] = useState("");
  const [oneClickToStopReRender, setOneClickToStopReRender] = useState(true);
  const editorRef = useRef(null);
  const emojiButtonRef = useRef(null);

  const formatText = (command, value = null) => {
    if (!editorRef.current) return;

    document.execCommand(command, false, value);
    editorRef.current.focus();

    updateFormatState(command, value);
  };


  const updateFormatState = (command, value) => {
    switch (command) {
      case "bold":
        setFormatState((prev) => ({ ...prev, bold: !prev.bold }));
        break;
      case "italic":
        setFormatState((prev) => ({ ...prev, italic: !prev.italic }));
        break;
      case "underline":
        setFormatState((prev) => ({ ...prev, underline: !prev.underline }));
        break;
      case "strikeThrough":
        setFormatState((prev) => ({
          ...prev,
          strikeThrough: !prev.strikeThrough,
        }));
        break;
      case "justifyLeft":
        setFormatState((prev) => ({
          ...prev,
          justifyLeft: true,
          justifyCenter: false,
          justifyRight: false,
        }));
        break;
      case "justifyCenter":
        setFormatState((prev) => ({
          ...prev,
          justifyLeft: false,
          justifyCenter: true,
          justifyRight: false,
        }));
        break;
      case "justifyRight":
        setFormatState((prev) => ({
          ...prev,
          justifyLeft: false,
          justifyCenter: false,
          justifyRight: true,
        }));
        break;
      case "fontName":
        setFormatState((prev) => ({ ...prev, fontName: value }));
        break;
      case "fontSize":
        setFormatState((prev) => ({ ...prev, fontSize: value }));
        break;
      default:
        break;
    }

    setTimeout(checkFormatState, 10);
  };

  const checkFormatState = () => {
    const currentFontName = document.queryCommandValue("fontName");

    let normalizedFontName = currentFontName.replace(/["']/g, "").toLowerCase();
    let fontNameValue = "Arial"; 
    if (
      normalizedFontName.includes("times") ||
      normalizedFontName.includes("roman")
    ) {
      fontNameValue = "Times New Roman";
    } else if (normalizedFontName.includes("courier")) {
      fontNameValue = "Courier New";
    } else if (normalizedFontName.includes("georgia")) {
      fontNameValue = "Georgia";
    } else if (normalizedFontName.includes("verdana")) {
      fontNameValue = "Verdana";
    } else if (normalizedFontName.includes("arial")) {
      fontNameValue = "Arial";
    }
    setFormatState({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      strikeThrough: document.queryCommandState("strikeThrough"),
      justifyLeft: document.queryCommandState("justifyLeft"),
      justifyCenter: document.queryCommandState("justifyCenter"),
      justifyRight: document.queryCommandState("justifyRight"),
      fontName: fontNameValue,
      fontSize: document.queryCommandValue("fontSize") || "3",
    });

    if (editorRef.current) {
      if (whatsappConfiguration) {
        setHtmlContent(editorRef.current.innerHTML);
        handleConfigurationChange(
          "whatsAppMessage",
          editorRef.current.innerHTML
        );
      } else if (icon === "Outlook") {
        setHtmlContent(editorRef.current.innerHTML);
        handleConfigurationChange("outlookBody", editorRef.current.innerHTML);
      } else {
        setHtmlContent(editorRef.current.innerHTML);
        handleConfigurationChange("gmailBody", editorRef.current.innerHTML);
      }
    } else {
      console.warn("editorRef.current ==>", editorRef.current);
    }
  };

  const insertEmoji = (emoji) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(emoji));
    }
    editorRef.current.focus();
    setShowEmojiPicker(false);
  };

  const emojis = ["😀", "😂", "❤️", "👍", "🎉", "🔥", "✅", "⭐", "🚀", "💡"];

  const saveDocument = () => {
    handleConfigurationChange("htmlContent", htmlContent);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(event.target) &&
        showEmojiPicker
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
    const handleClickInside = (event) => {
      if (editorRef.current && editorRef.current.contains(event.target)) {
        checkFormatState();
      }
    };

    const editorElement = editorRef.current;
    if (editorElement) {
      editorElement.addEventListener("click", handleClickInside);
    }

    return () => {
      if (editorElement) {
        editorElement.removeEventListener("click", handleClickInside);
      }
    };
  }, [editorRef]);

  useEffect(() => {
    if (whatsappConfiguration) {
      if (
        configuration?.whatsAppMessage &&
        JSON.stringify(configuration?.whatsAppMessage) !==
          JSON.stringify(htmlContent) &&
        oneClickToStopReRender
      ) {
        setHtmlContent(configuration?.whatsAppMessage);
        editorRef.current.innerHTML = configuration?.whatsAppMessage;
      }
    } else if (icon === "Outlook") {
      if (
        configuration?.outlookBody &&
        JSON.stringify(configuration?.outlookBody) !==
          JSON.stringify(htmlContent) &&
        oneClickToStopReRender
      ) {
        setHtmlContent(configuration?.outlookBody);
        editorRef.current.innerHTML = configuration?.outlookBody;
      }
    } else {
      if (
        configuration?.gmailBody &&
        JSON.stringify(configuration?.gmailBody) !==
          JSON.stringify(htmlContent) &&
        oneClickToStopReRender
      ) {
        setHtmlContent(configuration?.gmailBody);
        editorRef.current.innerHTML = configuration?.gmailBody;
      }
    }
  }, [
    configuration?.gmailBody,
    configuration?.whatsAppMessage,
    configuration?.outlookBody,
  ]);

  const [turnSize, setTurnSize] = useState(false);
  const [turnFont, setTurnFont] = useState(false);
  const [turnColor, setTurnColor] = useState(false);

  const dispatch = useDispatch();

  const handleSenEmail = () => {
    dispatch(
      sendEmailNotify({
        senderEmail: configuration?.selectedEmailCustomNotify.email,
        appPassword: configuration?.selectedEmailCustomNotify.appPassword,
        recipientEmail: configuration?.gmailTo[0],
        subject: configuration?.gmailSubject,
        htmlContent: configuration?.gmailBody,
      })
    );
  };
  useEffect(() => {
    if (editorRef.current && initialContent && oneClickToStopReRender) {
      editorRef.current.innerHTML = initialContent;
      setHtmlContent(initialContent);
      setOneClickToStopReRender(false); 
    }
  }, [initialContent, oneClickToStopReRender]);
  
  return (
    <div className={styles.wordApp} style={{ position: "relative", height:autoResize && 'fit-content' }}>
      <div className={styles.toolbar}>
        <div
          className={styles.toolGroup}
          id="idColorContainer"
          style={{
            height: "25px",
            padding: "0px 5px",
            position: "relative",
            alignItems: "end",
          }}
          onClick={() => {
            document.getElementById("idColor").click();
          }}
        >
          <A
            height={10}
            width={10}
            style={{
              position: "absolute",
              top: "3px",
              left: "12px",
              pointerEvents: "none",
            }}
          />
          <input
            id="idColor"
            type="color"
            onChange={(e) => {
              formatText("foreColor", e.target.value);
              setTurnColor(true);
            }}
            className={styles.colorPicker}
            title="Color de texto"
            onBlur={() => setTurnColor(false)}
            style={{
              border: "0px solid #ddd",
              padding: "0px",
              margin: "0px",
              height: "14px",
              outline: "0px solid #ddd",
            }}
          />
        </div>

        <div
          className={styles.fontSelect}
          style={{
            position: "relative",
          }}
          onClick={() => {
            document.getElementById("fontSizeSelect").click();
            setTurnSize(!turnSize);
          }}
        >
          <select
            onChange={(e) => {
              formatText("fontSize", e.target.value);
              setTurnSize(false);
            }}
            className={styles.sizeSelect}
            value={formatState.fontSize}
            id="fontSizeSelect"
            onBlur={() => setTurnSize(!turnSize)}
          >
            {[1, 2, 3, 4, 5, 6, 7].map((size) => (
              <option key={size} value={size}>
                {size + 8}
              </option>
            ))}
          </select>
          <ArrowDownGray
            height={10}
            width={10}
            style={{
              transform: !turnSize ? "rotate(180deg)" : "",
              transition: "transform 0.3s ease-in-out",
              position: "absolute",
              right: "5px",
              top: "8px",
              pointerEvents: "none",
            }}
            stroke="#1e1e20"
          />
        </div>

        <div
          className={styles.fontSelect}
          style={{
            position: "relative",
          }}
          onClick={() => {
            document.getElementById("fontSelect").click();
            setTurnFont(!turnFont);
          }}
        >
          <select
            onChange={(e) => {
              formatText("fontName", e.target.value);
              setTurnFont(false);
            }}
            className={styles.fontSelect}
            value={formatState.fontName}
            id="fontSelect"
            onBlur={() => setTurnFont(!turnFont)}
          >
            <option value="Arial" style={{ fontFamily: "Arial" }}>
              Arial
            </option>
            <option
              value="Times New Roman"
              style={{ fontFamily: "Times New Roman" }}
            >
              T. New Roman
            </option>
            <option value="Courier New" style={{ fontFamily: "Courier New" }}>
              Courier New
            </option>
            <option value="Georgia" style={{ fontFamily: "Georgia" }}>
              Georgia
            </option>
            <option value="Verdana" style={{ fontFamily: "Verdana" }}>
              Verdana
            </option>
          </select>
          <ArrowDownGray
            height={10}
            width={10}
            style={{
              transform: !turnFont ? "rotate(180deg)" : "",
              transition: "transform 0.3s ease-in-out",
              position: "absolute",
              right: "5px",
              top: "8px",
              pointerEvents: "none",
            }}
            stroke="#1e1e20"
          />
        </div>

        <button
          onClick={() => formatText("bold")}
          className={`${styles.toolButton} ${
            formatState.bold ? styles.active : ""
          }`}
          title="Negrita"
        >
          <Negrita />
        </button>
        <button
          onClick={() => formatText("italic")}
          className={`${styles.toolButton} ${
            formatState.italic ? styles.active : ""
          }`}
          title="Cursiva"
        >
          <Cursive />
        </button>
        <button
          onClick={() => formatText("underline")}
          className={`${styles.toolButton} ${
            formatState.underline ? styles.active : ""
          }`}
          title="Subrayado"
        >
          <Subrayado />
        </button>
        <button
          onClick={() => formatText("strikeThrough")}
          className={`${styles.toolButton} ${
            formatState.strikeThrough ? styles.active : ""
          }`}
          title="Tachado"
        >
          <MiddleLine />
        </button>

        <button
          onClick={() => formatText("justifyLeft")}
          className={`${styles.toolButton} ${
            formatState.justifyLeft ? styles.active : ""
          }`}
          title="Alinear a la izquierda"
        >
          <LeftSentence />
        </button>
        <button
          onClick={() => formatText("justifyCenter")}
          className={`${styles.toolButton} ${
            formatState.justifyCenter ? styles.active : ""
          }`}
          title="Centrar"
        >
          <CenterSentence />
        </button>
        <button
          onClick={() => formatText("justifyRight")}
          className={`${styles.toolButton} ${
            formatState.justifyRight ? styles.active : ""
          }`}
          title="Alinear a la derecha"
        >
          <RightSentence />
        </button>

        <button
          onClick={() => formatText("insertOrderedList")}
          className={styles.toolButton}
          title="Viñeta numérica"
        >
          <ViñetaNumerica />
        </button>

        <button
          onClick={() => formatText("insertUnorderedList")}
          className={styles.toolButton}
          title="Viñeta simple"
        >
          <ViñetaNumerica />
        </button>

        <button
          onClick={() => {    
            const isCodeBlock =
              document.queryCommandValue("formatBlock") === "pre";

            if (isCodeBlock) {
              formatText("formatBlock", "<p>");
            } else {
              formatText("formatBlock", "<pre>");
            }
          }}
          className={`${styles.toolButton} ${
            document.queryCommandValue("formatBlock") === "pre"
              ? styles.active
              : ""
          }`}
          title="Formato código"
        >
          <CodeSimbol />
        </button>

        <button
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          className={styles.toolButton}
          title="Insertar emoji"
        >
          <EmojiSimbol />
        </button>
        {showEmojiPicker && (
          <div className={styles.emojiPickerPopup}>
            {emojis.map((emoji, index) => (
              <button
                key={index}
                onClick={() => insertEmoji(emoji)}
                className={styles.emojiButton}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
        <button
          onClick={() => handleSenEmail()}
          className={styles.toolButton}
          title="Guardar"
        >
          A
        </button>
      </div>

      <div
        ref={editorRef}
        className={`${styles.editor} ${autoResize && styles.autoResize}`}
        contentEditable={true}
        onInput={checkFormatState}
        suppressContentEditableWarning={true}
        onClick={() => setOneClickToStopReRender(false)}
      ></div>
    </div>
  );
}

export default MiniWordDocs;
